import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SEOHead } from "@/hooks/useSEO";
import seoConfig from "@/config/seo.json";
import {
  StructuredDataHead,
  LLMMetadataHead,
  organizationSchema,
  softwareSchema,
  createWebPageSchema,
  createProductSchema,
} from "@/hooks/useStructuredData";
import { getProductPageBySlug } from "@/data/productPages";
import { useLanguage } from "@/hooks/useLanguage";

// AI Agent Plattform Components
import AIAgentPlattformHero from "@/components/landing/ai-agent-plattform/AIAgentPlattformHero";
import WarumPlattformSection from "@/components/landing/ai-agent-plattform/WarumPlattformSection";
import WissensbasisFundamentSection from "@/components/landing/ai-agent-plattform/WissensbasisFundamentSection";
import AIAgentsRollenSection from "@/components/landing/ai-agent-plattform/AIAgentsRollenSection";
import KommunikationAutomationSection from "@/components/landing/ai-agent-plattform/KommunikationAutomationSection";
import CapabilityProofsSection from "@/components/landing/ai-agent-plattform/CapabilityProofsSection";
import FAQPlattformSection from "@/components/landing/ai-agent-plattform/FAQPlattformSection";
import CTASection from "@/components/landing/CTASection";

// AI Agent Components
import AIAgentHero from "@/components/landing/ai-agent/AIAgentHero";
import WarumAIAgentsSection from "@/components/landing/ai-agent/WarumAIAgentsSection";
import AIAgentsDetailSection from "@/components/landing/ai-agent/AIAgentsDetailSection";
import KanaeleSection from "@/components/landing/ai-agent/KanaeleSection";
import BrandingPersonaSection from "@/components/landing/ai-agent/BrandingPersonaSection";
import FAQAgentSection from "@/components/landing/ai-agent/FAQAgentSection";

// AI Knowledge Base Components
import KnowledgeBaseHero from "@/components/landing/ai-knowledge-base/KnowledgeBaseHero";
import WarumWissensdatenbankSection from "@/components/landing/ai-knowledge-base/WarumWissensdatenbankSection";
import ZweiWissensartenSection from "@/components/landing/ai-knowledge-base/ZweiWissensartenSection";
import PlatformVisualizationSection from "@/components/landing/ai-knowledge-base/PlatformVisualizationSection";
import SteuerungQualitaetSection from "@/components/landing/ai-knowledge-base/SteuerungQualitaetSection";
import SchnellEingefuehrtSection from "@/components/landing/ai-knowledge-base/SchnellEingefuehrtSection";
import FAQKnowledgeBaseSection from "@/components/landing/ai-knowledge-base/FAQKnowledgeBaseSection";

// AI Automation Workflows Components
import AutomationWorkflowsHero from "@/components/landing/ai-automation-workflows/AutomationWorkflowsHero";
import WarumAutomationSection from "@/components/landing/ai-automation-workflows/WarumAutomationSection";
import FlowBuilderFunktionenSection from "@/components/landing/ai-automation-workflows/FlowBuilderFunktionenSection";
import IntegrationAutomationSection from "@/components/landing/ai-automation-workflows/IntegrationAutomationSection";
import FAQAutomationSection from "@/components/landing/ai-automation-workflows/FAQAutomationSection";

// Unified Inbox Components
import OmnichannelInboxHero from "@/components/landing/omnichannel-inbox/OmnichannelInboxHero";
import WarumOmnichannelSection from "@/components/landing/omnichannel-inbox/WarumOmnichannelSection";
import InboxFeaturesSection from "@/components/landing/omnichannel-inbox/InboxFeaturesSection";
import EineInboxSection from "@/components/landing/omnichannel-inbox/EineInboxSection";
import FAQOmnichannelSection from "@/components/landing/omnichannel-inbox/FAQOmnichannelSection";

// Analytics Components
import AnalyticsHero from "@/components/landing/analytics/AnalyticsHero";
import AnalyticsInteraktionenSection from "@/components/landing/analytics/AnalyticsInteraktionenSection";
import AnalyticsQualitaetSection from "@/components/landing/analytics/AnalyticsQualitaetSection";
import AnalyticsProzesseSection from "@/components/landing/analytics/AnalyticsProzesseSection";
import AnalyticsNachfrageSection from "@/components/landing/analytics/AnalyticsNachfrageSection";
import AnalyticsSteuerzentraleSection from "@/components/landing/analytics/AnalyticsSteuerzentraleSection";
import FAQAnalyticsSection from "@/components/landing/analytics/FAQAnalyticsSection";

// Enterprise Hosting (On-Premise) Components
import OnPremiseHero from "@/components/landing/on-premise/OnPremiseHero";
import OnPremiseChallengesSection from "@/components/landing/on-premise/OnPremiseChallengesSection";
import OnPremiseDeploymentSection from "@/components/landing/on-premise/OnPremiseDeploymentSection";
import OnPremiseBenefitsSection from "@/components/landing/on-premise/OnPremiseBenefitsSection";
import FAQOnPremiseSection from "@/components/landing/on-premise/FAQOnPremiseSection";

type PageKey = keyof typeof seoConfig.pages;

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const pageData = slug ? getProductPageBySlug(slug) : undefined;

  if (!pageData) {
    return <Navigate to="/404" replace />;
  }

  const renderContent = () => {
    switch (pageData.componentSet) {
      case "ai-agent-plattform":
        return (
          <>
            <AIAgentPlattformHero />
            <WarumPlattformSection />
            <WissensbasisFundamentSection />
            <AIAgentsRollenSection />
            <KommunikationAutomationSection />
            <CapabilityProofsSection />
            <FAQPlattformSection />
            <CTASection />
          </>
        );
      case "ai-agent":
        return (
          <>
            <AIAgentHero />
            <WarumAIAgentsSection />
            <AIAgentsDetailSection />
            <KanaeleSection />
            <BrandingPersonaSection />
            <FAQAgentSection />
            <CTASection />
          </>
        );
      case "ai-knowledge-base":
        return (
          <>
            <KnowledgeBaseHero />
            <WarumWissensdatenbankSection />
            <ZweiWissensartenSection />
            <PlatformVisualizationSection />
            <SteuerungQualitaetSection />
            <SchnellEingefuehrtSection />
            <FAQKnowledgeBaseSection />
            <CTASection />
          </>
        );
      case "ai-automation-workflows":
        return (
          <>
            <AutomationWorkflowsHero />
            <WarumAutomationSection />
            <FlowBuilderFunktionenSection />
            <IntegrationAutomationSection />
            <FAQAutomationSection />
            <CTASection />
          </>
        );
      case "unified-inbox":
        return (
          <>
            <OmnichannelInboxHero />
            <WarumOmnichannelSection />
            <InboxFeaturesSection />
            <EineInboxSection />
            <FAQOmnichannelSection />
            <CTASection />
          </>
        );
      case "analytics":
        return (
          <>
            <AnalyticsHero />
            <AnalyticsSteuerzentraleSection />
            <AnalyticsInteraktionenSection />
            <AnalyticsNachfrageSection />
            <AnalyticsProzesseSection />
            <AnalyticsQualitaetSection />
            <FAQAnalyticsSection />
            <CTASection />
          </>
        );
      case "enterprise-hosting":
        return (
          <>
            <OnPremiseHero />
            <OnPremiseChallengesSection />
            <OnPremiseDeploymentSection />
            <OnPremiseBenefitsSection />
            <FAQOnPremiseSection />
            <CTASection />
          </>
        );
      default:
        return <Navigate to="/404" replace />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page={(pageData.seoKey || "home") as PageKey} locale={language} />
      <StructuredDataHead schemas={[
        organizationSchema,
        softwareSchema,
        createWebPageSchema(
          pageData.title,
          pageData.description,
          `/produkte/${pageData.slug}`,
          [
            { name: "Home", url: "/" },
            { name: "Produkte", url: "/" },
            { name: pageData.breadcrumbLabel, url: `/produkte/${pageData.slug}` },
          ]
        ),
        createProductSchema({
          name: `Chathero ${pageData.breadcrumbLabel}`,
          description: pageData.description,
          url: `/produkte/${pageData.slug}`,
          features: pageData.features,
        }),
      ]} />
      <LLMMetadataHead
        title={pageData.title}
        description={pageData.description}
        keywords={pageData.keywords}
        canonicalUrl={pageData.canonicalUrl}
        aiCategory="SaaS, AI Product, Customer Communication"
        aiEntityType="Product Page"
        aiUseCase={pageData.features.join(", ")}
        aiFeatures={pageData.features.join(", ")}
      />
      <Header />
      <main>{renderContent()}</main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
